'use client'

import { useLenis } from 'lenis/react'
import { useCallback, useLayoutEffect, useRef } from 'react'

import {
  clearHeroChatLauncherBottomInset,
  HERO_CHAT_LAUNCHER_SLOT_MEDIA_QUERY,
  measureRaisedHeroChatBottomInset,
  readSitePromoChromeBottomPx,
  resolveClosedChatBottomPx,
  setHeroChatLauncherBottomInset,
} from '@/lib/chatbot/heroChatLauncherInset'
import { SITE_PROMO_CHROME_BOTTOM_EVENT } from '@/lib/promotion-banner/sitePromoChromeInset'

type HeroChatLauncherSlotProps = {
  /** When false, the slot collapses so the title can sit on the banner and chat docks normally. */
  active?: boolean
}

/**
 * Reserves a closed-chat row above the hero H1 on mobile and publishes its
 * viewport offset so the fixed launcher can sit in that row: chat, then title,
 * then promo banner. Desktop keeps the previous layout (title above the banner,
 * launcher docked to the promo chrome).
 */
export function HeroChatLauncherSlot({ active = true }: HeroChatLauncherSlotProps) {
  const placeholderRef = useRef<HTMLDivElement>(null)
  const lastInsetRef = useRef<number | null>(null)
  const lenis = useLenis()

  const syncInset = useCallback(() => {
    if (!active || !window.matchMedia(HERO_CHAT_LAUNCHER_SLOT_MEDIA_QUERY).matches) {
      lastInsetRef.current = null
      clearHeroChatLauncherBottomInset()
      return
    }

    const placeholder = placeholderRef.current
    if (!placeholder) {
      return
    }

    const raisedPx = measureRaisedHeroChatBottomInset(placeholder)
    const chromePx = readSitePromoChromeBottomPx()
    if (raisedPx <= 0 && chromePx <= 0) {
      return
    }

    const nextInset = resolveClosedChatBottomPx(Math.max(raisedPx, chromePx), chromePx)
    if (lastInsetRef.current === nextInset) {
      return
    }

    lastInsetRef.current = nextInset
    setHeroChatLauncherBottomInset(nextInset)
  }, [active])

  useLayoutEffect(() => {
    if (!active) {
      lastInsetRef.current = null
      clearHeroChatLauncherBottomInset()

      return () => {
        lastInsetRef.current = null
        clearHeroChatLauncherBottomInset()
      }
    }

    let cancelled = false
    const syncIfMounted = () => {
      if (!cancelled) {
        syncInset()
      }
    }

    syncIfMounted()

    const placeholder = placeholderRef.current
    if (!placeholder) {
      return () => {
        cancelled = true
        lastInsetRef.current = null
        clearHeroChatLauncherBottomInset()
      }
    }

    const heading = placeholder.parentElement?.nextElementSibling
    const resizeObserver = new ResizeObserver(syncIfMounted)
    resizeObserver.observe(placeholder)
    if (placeholder.parentElement) {
      resizeObserver.observe(placeholder.parentElement)
    }
    if (heading instanceof HTMLElement) {
      resizeObserver.observe(heading)
    }

    const mutationObserver = new MutationObserver(syncIfMounted)
    mutationObserver.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['style'],
    })

    window.addEventListener('resize', syncIfMounted)
    const slotMedia = window.matchMedia(HERO_CHAT_LAUNCHER_SLOT_MEDIA_QUERY)
    slotMedia.addEventListener('change', syncIfMounted)
    document.documentElement.addEventListener(SITE_PROMO_CHROME_BOTTOM_EVENT, syncIfMounted)
    void document.fonts?.ready.then(syncIfMounted)

    const rafId = window.requestAnimationFrame(() => {
      syncIfMounted()
      window.requestAnimationFrame(syncIfMounted)
    })

    if (lenis) {
      lenis.on('scroll', syncIfMounted)
    } else {
      window.addEventListener('scroll', syncIfMounted, { passive: true })
    }

    return () => {
      cancelled = true
      window.cancelAnimationFrame(rafId)
      resizeObserver.disconnect()
      mutationObserver.disconnect()
      window.removeEventListener('resize', syncIfMounted)
      slotMedia.removeEventListener('change', syncIfMounted)
      document.documentElement.removeEventListener(SITE_PROMO_CHROME_BOTTOM_EVENT, syncIfMounted)
      if (lenis) {
        lenis.off('scroll', syncIfMounted)
      } else {
        window.removeEventListener('scroll', syncIfMounted)
      }
      lastInsetRef.current = null
      clearHeroChatLauncherBottomInset()
    }
  }, [active, lenis, syncInset])

  if (!active) {
    return null
  }

  return (
    <div aria-hidden className="flex w-full shrink-0 justify-end -mb-10 md:hidden">
      <div className="size-[100px] shrink-0" ref={placeholderRef} />
    </div>
  )
}
